
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useEffect } from 'react';

const Professionnel = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/">
              <Button variant="ghost" className="mb-4">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour à l'accueil
              </Button>
            </Link>
          </div>

          {/* Hero Section */}
          <div className="text-center mb-16 reveal-on-scroll">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-8">🌟 L'Espace Professionnel – Recevoir pour mieux accompagner</h1>
            
            <div className="max-w-4xl mx-auto space-y-6 text-lg leading-relaxed">
              <p>
                Tu accompagnes les autres. Tu tiens l'espace. Tu donnes.<br />
                Mais… où poses-tu ce que tu traverses ? Qui t'accompagne, toi ?
              </p>
              
              <p className="text-xl text-primary font-medium">
                🌿 Ici, tu peux te déposer.<br />
                Respirer. T'aligner. Recevoir.<br />
                Je t'ouvre un espace stable, vibratoire, clair — pour revenir à ton axe, sans masque, sans sur-effort.
              </p>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            
            {/* Hypnose régressive */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">🌀 Hypnose régressive – Libération des racines profondes</CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/81739330-c321-4d85-8384-6b903b35f39e.png" 
                  alt="Hypnose régressive pour professionnels" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Une hypnose ciblée pour aller explorer les racines de ce que tu traverses. Mémoire ancienne, loyauté, peur enfouie… Comprendre, libérer, réintégrer.
                </p>
                <div className="text-sm text-foreground/70">
                  <p>⏳ 1h • 💸 80 € • En visio ou à Étampes</p>
                </div>
              </CardContent>
              <CardFooter>
                <a href="https://calendly.com/meneora" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="btn-primary w-full flex gap-2 items-center">
                    <Calendar className="h-4 w-4" /> Réserver cette séance
                  </Button>
                </a>
              </CardFooter>
            </Card>

            {/* Soin énergétique */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">💠 Soin énergétique – Nettoyage, réalignement, recharge</CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/7a03015c-8263-4d46-94a2-1647d315ab39.png" 
                  alt="Soin énergétique pour professionnels" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Un soin vibratoire pour te recentrer, te recharger, réaligner ton système énergétique après l'accompagnement des autres.
                </p>
                <div className="text-sm text-foreground/70">
                  <p>⏳ 1h à 1h15 • 💸 80 € • Présentiel ou à distance</p>
                </div>
              </CardContent>
              <CardFooter>
                <a href="https://calendly.com/meneora/soinenergetiquepro" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="btn-primary w-full flex gap-2 items-center">
                    <Calendar className="h-4 w-4" /> Réserver un soin
                  </Button>
                </a>
              </CardFooter>
            </Card>

            {/* Hypnose + soin vibratoire */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">🌙 Hypnose + soin vibratoire – Séance intégrale</CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/35004b11-ebbc-4567-8bfd-dbc23472206d.png" 
                  alt="Hypnose et soin vibratoire intégral" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Hypnose ciblée + soin énergétique profond. Une séance complète pour libérer, réaligner, réintégrer.
                </p>
                <div className="text-sm text-foreground/70">
                  <p>⏳ 2h • 💸 130 € • En visio ou à Étampes</p>
                </div>
              </CardContent>
              <CardFooter>
                <a href="https://calendly.com/meneora/hypnose-therapeutique-pro" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="btn-primary w-full flex gap-2 items-center">
                    <Calendar className="h-4 w-4" /> Prendre rendez-vous
                  </Button>
                </a>
              </CardFooter>
            </Card>

            {/* Accompagnement Rayon.née */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">🔥 Accompagnement Rayon.née – Renaissance vibratoire</CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/72665b79-799a-4fe6-a0d6-6e41baf738ec.png" 
                  alt="Accompagnement Rayon.née pour professionnels" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Un accompagnement de 4 mois pour revenir à ta souveraineté vibratoire. Soins, transmissions, hypnose, guidance personnalisée.
                </p>
                <div className="text-sm text-foreground/70">
                  <p>✨ Finançable via CPF • ⏳ 4 mois • 💸 2 400 € • En visio ou en présentiel</p>
                </div>
              </CardContent>
              <CardFooter>
                <a href="https://calendly.com/meneora/accompagnement-sur-4-mois-financable-par-le-cpf" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="btn-primary w-full flex gap-2 items-center">
                    <Calendar className="h-4 w-4" /> Découvrir Rayon.née
                  </Button>
                </a>
              </CardFooter>
            </Card>

            {/* Nettoyage énergétique de lieux */}
            <Card className="card-soft h-full flex flex-col reveal-on-scroll">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">🏠 Nettoyage énergétique de lieux – Harmonisation vibratoire</CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <img 
                  src="/lovable-uploads/f7889efd-b399-4fc7-81c3-9e5b3865d760.png" 
                  alt="Nettoyage énergétique de lieux professionnels" 
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
                <p className="text-foreground/80 mb-4">
                  Je nettoie ton cabinet, ton espace professionnel ou ton lieu de vie. Connexion à l'espace et à ses occupants pour rééquilibrer et transmettre des outils d'entretien vibratoire.
                </p>
                <div className="text-sm text-foreground/70">
                  <p>⏳ 1h à 2h • 💸 à partir de 180 € • En visio ou à domicile</p>
                </div>
              </CardContent>
              <CardFooter>
                <a href="https://calendly.com/meneora/nettoyage-de-lieux" target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="btn-primary w-full flex gap-2 items-center">
                    <Calendar className="h-4 w-4" /> Demander un nettoyage
                  </Button>
                </a>
              </CardFooter>
            </Card>

          </div>

          {/* Call to Action */}
          <div className="bg-accent/10 p-8 rounded-lg text-center reveal-on-scroll">
            <h2 className="text-2xl font-semibold text-primary mb-6">Tu ne sais pas par quoi commencer ?</h2>
            <p className="text-lg mb-6">
              Tu peux simplement m'écrire. Je t'écoute.
            </p>
            <a href="https://wa.me/33607507412" target="_blank" rel="noopener noreferrer">
              <Button size="lg">
                Me contacter sur WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Professionnel;
